import { useParams } from "react-router-dom";

import { useRecoilValue } from "recoil";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { type Container } from "../../core/models";
import { organizationsSelector } from "../../core/recoil/organizations";
import { CofConfigId, type ResourceContainer } from "../../gql-generated/types";

import { useOrganizationsV2 } from "./useOrganizationsV2";
import { SearchParamKeys, useSearchParams } from "./useSearchParams";
import { LensFoldersSearchParams } from "./useSelectedLensFolderV3";

interface OrganizationContainerMeta {
    uniqueId: string | undefined;
    name: string;
    organizationName: string;
    orgId: string | undefined;
    lensFolderId: string | undefined;
    container: Container | ResourceContainer | undefined;
}

export const useSelectedOrganizationContainer = (): OrganizationContainerMeta => {
    const { orgId: organizationIdFromParams } = useParams<{ orgId?: string }>();
    const { lensFolderId: lensFolderIdFromParams } = useParams<{ lensFolderId?: string }>();
    const searchParams = useSearchParams();
    const lensFolderId = lensFolderIdFromParams ?? searchParams.get(LensFoldersSearchParams.LensFolder) ?? undefined;
    const orgId = organizationIdFromParams ?? searchParams.get(SearchParamKeys.Organization) ?? undefined;

    const { containersIndex } = useRecoilValue(organizationsSelector);
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const { containersIndex: containersIndexV2 } = useOrganizationsV2();

    const uniqueId = lensFolderId ?? orgId;
    let container: Container | ResourceContainer | undefined = uniqueId ? containersIndex.get(uniqueId) : undefined;
    let organizationContainer: Container | ResourceContainer | undefined = containersIndex.get(orgId ?? "");

    if (isMyLensesOrgsV2Enabled) {
        container = uniqueId ? containersIndexV2.get(uniqueId) : undefined;
        organizationContainer = containersIndexV2.get(orgId ?? "");
    }

    const getOrganizationId = (c: Container | ResourceContainer | undefined): string | undefined => {
        if (!c) return undefined;
        if ("organizationId" in c) return c.organizationId;
        if ("parentName" in c) return c.parentName;
        return undefined;
    };

    return {
        uniqueId,
        organizationName: organizationContainer?.name ?? "",
        name: container?.name ?? "",
        orgId: orgId ?? getOrganizationId(container),
        lensFolderId,
        container,
    };
};
